// src/components/connectors/shared.tsx
//
// DEM-2118 (Marketing F4b) — módulo COMPARTILHADO de conectores.
//
// Extraído de ConnectorsPage.tsx (DEM-2120/F2 + DEM-2124/F3) para permitir que a
// sub-aba "Redes Sociais" do business (BusinessSocialTab) reuse os MESMOS fluxos
// de conexão (OAuth2 / api_key / MCP), o mascaramento de credencial e a leitura
// do external_account — sem reimplementar nada (decisão de consenso do PR).
//
// Aqui vivem: vocabulário de UI (labels/status), leitores do external_account e
// o <ConnectModal/> (o formulário de conectar/reconectar). ConnectorsPage e
// BusinessSocialTab importam daqui — SINGLE SOURCE OF TRUTH, zero duplicação.

import { useState, type ReactNode } from 'react';
import {
  api,
  type ConnectorCatalogItem,
  type ConnectorAuthMode,
  type Business,
} from '@/lib/api';
import {
  X,
  Plug,
  ShieldCheck,
  ExternalLink,
  DollarSign,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { MODE_LABELS, CONNECTABLE_MODES } from './connectors-ui';

// ─── Guia de setup: linkificação de URLs ──────────────────────────────────────
//
// Zero fricção: todo "Acesse https://..." do guia vira âncora clicável em NOVA
// ABA — o usuário cai direto no console da plataforma, sem copiar/caçar
// endereço. Regex captura https?:// até espaço/pontuação de fecho.
function renderGuideWithLinks(text: string): ReactNode[] {
  return text.split(/(https?:\/\/[^\s)\]",>]+)/g).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-raios-accent-text underline underline-offset-2 hover:opacity-80 break-all"
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
}

// ─── Modal de conexão (api_key / MCP / OAuth2 + vínculo) ──────────────────────
//
// `presetBusinessId` (DEM-2118): quando definido, a conexão nasce SEMPRE atrelada
// a esse business e o seletor de vínculo é omitido — é o modo usado pela sub-aba
// Redes Sociais do business, onde o vínculo é implícito (business_id da aba).

export function ConnectModal({
  connector,
  businesses,
  onClose,
  onConnected,
  reconnectConnectionId,
  presetBusinessId,
  redirectUri,
}: {
  connector: ConnectorCatalogItem;
  businesses: Business[];
  onClose: () => void;
  onConnected: () => Promise<void> | void;
  /** Reconexão OAuth de uma conexão expirada. */
  reconnectConnectionId?: string | null;
  /** DEM-2118: crava o vínculo a um business e esconde o seletor de vínculo. */
  presetBusinessId?: string | null;
  /**
   * DEM-2128: redirect URI exata do callback central do Core (por instalação).
   * O usuário copia essa URI pro console do app dele (Meta/Google/TikTok/LinkedIn).
   * Vem do catálogo (api.getConnectorCatalog().redirect_uri).
   */
  redirectUri?: string | null;
}) {
  const { toast } = useToast();

  // Modos conectáveis (F3 habilita oauth2). Prioriza oauth2 quando disponível.
  const modes = connector.supported_modes.filter((m) => CONNECTABLE_MODES.includes(m));
  const preferredMode =
    modes.includes('oauth2') ? 'oauth2' : modes.includes('mcp') ? 'mcp' : modes[0] ?? 'api_key';
  const [mode, setMode] = useState<ConnectorAuthMode>(preferredMode);

  const [displayName, setDisplayName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [mcpServer, setMcpServer] = useState(
    (connector.mcp_config?.server as string | undefined) ?? '',
  );
  // X-MCP budget.
  const [budgetUsd, setBudgetUsd] = useState<string>(
    String((connector.mcp_config?.budget_default_usd as number | undefined) ?? 10),
  );
  // DEM-2128: TODOS os conectores oauth2 são BYO (app do usuário). O modal coleta
  // client_id/client_secret e exibe o guia de setup + a redirect URI copiável.
  const byoApp = connector.oauth_config?.byo_app === true;
  const [byoClientId, setByoClientId] = useState('');
  const [byoClientSecret, setByoClientSecret] = useState('');
  const setupGuideMd =
    typeof connector.oauth_config?.setup_guide_md === 'string'
      ? connector.oauth_config.setup_guide_md
      : '';
  // DEM-2153 — guia por MODO: exibe o conteudo pertinente ao modo VISIVEL, nunca
  // um guia de outro modo (ex: no api_key do Meta Ads NAO mostrar o passo MCP,
  // que segue oculto na UI por ordem do admin). Resolve setup_guides[<mode>] e cai
  // no setup_guide_md unico (retrocompat com conectores single-mode: gmail, X...).
  const setupGuides = connector.oauth_config?.setup_guides;
  const guideForMode = (m: ConnectorAuthMode): string => {
    const perMode = setupGuides?.[m];
    if (typeof perMode === 'string' && perMode.length > 0) return perMode;
    return setupGuideMd;
  };
  // Nomes oficiais da plataforma (Meta "App ID"/"App Secret", TikTok "Client Key",
  // "System User Access Token") — vem do catalogo (oauth_config.credential_labels);
  // fallback generico quando ausentes.
  const credentialLabels = connector.oauth_config?.credential_labels;
  const clientIdLabel = credentialLabels?.client_id ?? 'Client ID';
  const clientSecretLabel = credentialLabels?.client_secret ?? 'Client Secret';
  const apiKeyLabel = credentialLabels?.api_key ?? 'Chave de API';
  const [guideOpen, setGuideOpen] = useState(false);
  const [copiedRedirect, setCopiedRedirect] = useState(false);

  async function copyRedirectUri() {
    if (!redirectUri) return;
    try {
      await navigator.clipboard.writeText(redirectUri);
      setCopiedRedirect(true);
      setTimeout(() => setCopiedRedirect(false), 1800);
    } catch {
      toast({ message: 'Não foi possível copiar. Copie manualmente.', type: 'error' });
    }
  }
  // vínculo: '' = conta do tenant (business_id NULL); uuid = business.
  // Quando presetBusinessId é passado, o vínculo é fixo e o seletor não aparece.
  const bindingLocked = presetBusinessId != null && presetBusinessId !== '';
  const [binding, setBinding] = useState<string>(presetBusinessId ?? '');

  const [submitting, setSubmitting] = useState(false);

  const isApiKey = mode === 'api_key';
  const isMcp = mode === 'mcp';
  const isOauth = mode === 'oauth2';
  const isXMcp = isMcp && connector.slug === 'x';
  // DEM-2124: MCP com handshake OAuth central (discovery + DCR + PKCE no
  // backend). X é o único MCP com registro local próprio (budget cravado).
  const isMcpOauth = isMcp && !isXMcp;

  const canSubmit =
    (isOauth || isMcpOauth
      ? displayName.length >= 0 &&
        // BYO client_id/secret so existe (e so renderiza) no fluxo oauth2 — o MCP
        // usa DCR (client dinamico, RFC 7591), sem credencial do usuario pra exigir.
        // Sem este guard, byo_app no catalogo trava o submit em modo MCP pra sempre
        // (bug meta-ads 2026-07-13).
        (!isOauth || !byoApp || (byoClientId.trim().length > 0 && byoClientSecret.trim().length > 0))
      : displayName.trim().length > 0 &&
        (isApiKey ? apiKey.trim().length > 0 : true) &&
        (isXMcp
          ? Number.isFinite(Number(budgetUsd)) && Number(budgetUsd) > 0
          : true)) &&
    !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // ─── OAuth2 — redireciona pro provider via authorize central.
      if (isOauth) {
        const { authorize_url } = await api.startConnectorOAuth({
          connector_slug: connector.slug,
          business_id: binding || null,
          reconnect_connection_id: reconnectConnectionId ?? null,
          byo_client_id: byoApp ? byoClientId.trim() : null,
          byo_client_secret: byoApp ? byoClientSecret.trim() : null,
        });
        // Redireciona o browser pro provider — o callback do Core volta pra cá.
        window.location.assign(authorize_url);
        return;
      }

      // ─── MCP (DEM-2124) — handshake OAuth central do MCP server: o backend
      // descobre o authorization server (RFC 9728/8414), registra client
      // dinâmico (RFC 7591) e devolve a authorize_url (PKCE). Clicar = autorizar
      // no provider (ex: Meta Business Suite); o callback do Core grava o token
      // no vault e volta pra cá. X fica fora (registro local com budget, abaixo).
      if (isMcpOauth) {
        const { authorize_url } = await api.startConnectorOAuth({
          connector_slug: connector.slug,
          business_id: binding || null,
          reconnect_connection_id: reconnectConnectionId ?? null,
          auth_mode: 'mcp',
        });
        window.location.assign(authorize_url);
        return;
      }

      // ─── X modo MCP — registra conexão com budget cravado.
      if (isXMcp) {
        await api.connectXMcp({
          business_id: binding || null,
          display_name: displayName.trim() || undefined,
          budget_usd: Number(budgetUsd),
          server: mcpServer.trim() || undefined,
        });
        toast({ message: `X conectado via MCP (budget ${budgetUsd}).`, type: 'success' });
        await onConnected();
        return;
      }

      // ─── api_key.
      const payload: Parameters<typeof api.createConnectorConnection>[0] = {
        connector_slug: connector.slug,
        display_name: displayName.trim(),
        auth_mode: mode,
        business_id: binding || null,
      };
      if (isApiKey) {
        // A chave vai como secret_value → backend cifra no vault. NUNCA re-exibida.
        payload.secret_value = apiKey.trim();
      }
      await api.createConnectorConnection(payload);
      // Higiene de segurança: descarta a chave da memória do componente.
      setApiKey('');
      toast({ message: `Conector "${connector.name}" conectado.`, type: 'success' });
      await onConnected();
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'Erro ao conectar.',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-modal-title"
    >
      <Card className="w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 id="connect-modal-title" className="text-lg font-semibold text-raios-text">
            {reconnectConnectionId ? 'Reconectar' : 'Conectar'} {connector.name}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-raios-muted hover:text-raios-text"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Escolha de modo (só quando há mais de um conectável) */}
        {modes.length > 1 && (
          <div>
            <label className="block text-xs font-medium text-raios-text-secondary mb-1.5">
              Modo de conexão
            </label>
            <div className="flex gap-2">
              {modes.map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mode === m}
                  onClick={() => setMode(m)}
                  className={
                    'text-xs px-3 py-1.5 rounded-md border transition-colors ' +
                    (mode === m
                      ? 'bg-raios-accent/15 text-raios-accent-text border-raios-accent/40'
                      : 'bg-raios-card text-raios-muted border-raios-border hover:text-raios-text')
                  }
                >
                  {MODE_LABELS[m]}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Nome da conexão — opcional no OAuth (handle vem do provider). */}
        {!isOauth && (
          <Input
            label="Nome da conexão"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={`ex: ${connector.name} — conta principal`}
          />
        )}

        {/* OAuth2 — redireciona pro provider. LinkedIn: app próprio (BYO). */}
        {isOauth && (
          <div className="space-y-3">
            {byoApp ? (
              <>
                <p className="text-[11px] text-raios-muted inline-flex items-start gap-1">
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden />
                  Este conector usa o SEU app OAuth. Cole o {clientIdLabel} e o {clientSecretLabel} do
                  seu app — são cifrados no vault e usados só para autenticar sua conta.
                </p>

                {/* Guia de setup in-modal (collapsible) — passo-a-passo pra criar o app. */}
                {guideForMode('oauth2') && (
                  <div className="rounded-md border border-raios-border bg-raios-card">
                    <button
                      type="button"
                      onClick={() => setGuideOpen((v) => !v)}
                      aria-expanded={guideOpen}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-raios-text-secondary hover:text-raios-text"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {guideOpen ? (
                          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                        )}
                        Como criar o app em {connector.name}
                      </span>
                    </button>
                    {guideOpen && (
                      <pre className="whitespace-pre-wrap px-3 pb-3 text-[11px] leading-relaxed text-raios-muted font-sans">
                        {renderGuideWithLinks(guideForMode('oauth2'))}
                      </pre>
                    )}
                  </div>
                )}

                {/* Redirect URI exata — copiável. O usuário cola no console do app. */}
                {redirectUri && (
                  <div>
                    <label className="block text-xs font-medium text-raios-text-secondary mb-1.5">
                      Redirect URI (cole no console do seu app)
                    </label>
                    <div className="flex items-stretch gap-2">
                      <code className="flex-1 min-w-0 truncate rounded-md border border-raios-border bg-raios-card px-3 py-2 text-[11px] text-raios-text">
                        {redirectUri}
                      </code>
                      <button
                        type="button"
                        onClick={copyRedirectUri}
                        aria-label="Copiar redirect URI"
                        className="inline-flex items-center gap-1 rounded-md border border-raios-border bg-raios-card px-2.5 text-xs text-raios-muted hover:text-raios-text"
                      >
                        {copiedRedirect ? (
                          <>
                            <Check className="h-3.5 w-3.5" aria-hidden /> Copiado
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5" aria-hidden /> Copiar
                          </>
                        )}
                      </button>
                    </div>
                    <p className="mt-1.5 text-[11px] text-raios-muted">
                      Essa é a URL de callback do RAIOS — registre-a EXATAMENTE assim nas URIs de
                      redirecionamento autorizadas do seu app.
                    </p>
                  </div>
                )}

                <Input
                  label={`${clientIdLabel} (seu app)`}
                  value={byoClientId}
                  onChange={(e) => setByoClientId(e.target.value)}
                  placeholder={`${clientIdLabel} do seu app`}
                  autoComplete="off"
                />
                <Input
                  label={`${clientSecretLabel} (seu app)`}
                  type="password"
                  value={byoClientSecret}
                  onChange={(e) => setByoClientSecret(e.target.value)}
                  placeholder={`${clientSecretLabel} do seu app`}
                  autoComplete="off"
                />
              </>
            ) : (
              <p className="text-[11px] text-raios-muted inline-flex items-start gap-1">
                <ExternalLink className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden />
                Você será redirecionado para {connector.name} autorizar o acesso. O token fica
                cifrado no vault do RAIOS — nunca é exibido.
              </p>
            )}
          </div>
        )}

        {/* X modo MCP — budget por conexão (pay-per-use). */}
        {isXMcp && (
          <div className="space-y-3">
            <div>
              <Input
                label="Budget (USD)"
                type="number"
                value={budgetUsd}
                onChange={(e) => setBudgetUsd(e.target.value)}
                placeholder="10"
              />
              <p className="mt-1.5 text-[11px] text-raios-muted inline-flex items-start gap-1">
                <DollarSign className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden />
                A API do X é paga por uso. O consumo é contado na conexão e você é alertado ao
                cruzar 80% do budget.
              </p>
            </div>
            <Input
              label="MCP server"
              value={mcpServer}
              onChange={(e) => setMcpServer(e.target.value)}
              placeholder="x-mcp"
            />
          </div>
        )}

        {/* Autenticação api_key */}
        {isApiKey && (
          <div className="space-y-3">
            {/* DEM-2153 — guia in-modal (collapsible) tambem no modo api_key: passo-a-passo
                pra OBTER a credencial (ex: System User token do Meta Ads). Reusa o mesmo
                componente collapsible + renderGuideWithLinks do oauth2/mcp. So exibe o guia
                pertinente ao MODO api_key (guideForMode('api_key')) — nunca conteudo de outro modo. */}
            {guideForMode('api_key') && (
              <div className="rounded-md border border-raios-border bg-raios-card">
                <button
                  type="button"
                  onClick={() => setGuideOpen((v) => !v)}
                  aria-expanded={guideOpen}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-raios-text-secondary hover:text-raios-text"
                >
                  <span className="inline-flex items-center gap-1.5">
                    {guideOpen ? (
                      <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                    )}
                    Como obter a credencial de {connector.name}
                  </span>
                </button>
                {guideOpen && (
                  <pre className="whitespace-pre-wrap px-3 pb-3 text-[11px] leading-relaxed text-raios-muted font-sans">
                    {renderGuideWithLinks(guideForMode('api_key'))}
                  </pre>
                )}
              </div>
            )}
            <div>
              <Input
                label={apiKeyLabel}
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="cole a chave aqui"
                autoComplete="off"
              />
              <p className="mt-1.5 text-[11px] text-raios-muted inline-flex items-start gap-1">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden />
                A chave é cifrada no vault do RAIOS e nunca é exibida de novo após salvar.
              </p>
            </div>
          </div>
        )}

        {/* MCP com handshake OAuth central (DEM-2124): sem campo manual — clicar
            em Conectar descobre o server oficial, registra client dinâmico e
            abre a autorização do provider (ex: Meta Business Suite). */}
        {isMcpOauth && (
          <div className="space-y-3">
            {guideForMode('mcp') && (
              <div className="rounded-md border border-raios-border">
                <button
                  type="button"
                  onClick={() => setGuideOpen((v) => !v)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-raios-text-secondary hover:text-raios-text"
                >
                  <span className="inline-flex items-center gap-1.5">
                    {guideOpen ? (
                      <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                    )}
                    Como funciona a conexão MCP
                  </span>
                </button>
                {guideOpen && (
                  <pre className="whitespace-pre-wrap px-3 pb-3 text-[11px] leading-relaxed text-raios-muted font-sans">
                    {renderGuideWithLinks(guideForMode('mcp'))}
                  </pre>
                )}
              </div>
            )}
            <p className="text-[11px] text-raios-muted inline-flex items-start gap-1">
              <ExternalLink className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden />
              Você será redirecionado para autorizar no provider do MCP server oficial —
              sem token manual. O acesso fica cifrado no vault do RAIOS e nunca é exibido.
            </p>
          </div>
        )}

        {/* Vínculo: conta do tenant ou business — omitido quando o vínculo é fixo. */}
        {bindingLocked ? (
          <p className="text-[11px] text-raios-muted inline-flex items-start gap-1">
            <Plug className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden />
            Conexão atrelada automaticamente a este negócio.
          </p>
        ) : (
          <div>
            <label
              htmlFor="connect-binding"
              className="block text-xs font-medium text-raios-text-secondary mb-1.5"
            >
              Vínculo da conexão
            </label>
            <select
              id="connect-binding"
              value={binding}
              onChange={(e) => setBinding(e.target.value)}
              className="w-full bg-raios-card border border-raios-border rounded-md text-sm text-raios-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-raios-accent/40"
            >
              <option value="">Conta do tenant (todos os negócios)</option>
              {businesses.map((b) => (
                <option key={b.id} value={b.id}>
                  Negócio: {b.name}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-[11px] text-raios-muted">
              {binding
                ? 'Conexão atrelada a este negócio.'
                : 'Conexão no nível da conta — disponível para todos os negócios.'}
            </p>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            variant="brand"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit}
            className="flex-1"
          >
            {isOauth ? (
              <>
                <ExternalLink className="h-3.5 w-3.5" />{' '}
                {reconnectConnectionId ? 'Reconectar' : 'Autorizar'}
              </>
            ) : (
              <>
                <Plug className="h-3.5 w-3.5" /> {reconnectConnectionId ? 'Reconectar' : 'Conectar'}
              </>
            )}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
        </div>
      </Card>
    </div>
  );
}
